import React, { useState } from "react";
import moment from "moment";
import _ from "lodash";
import { breaks } from "../../utils/Helpers";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Coffee, Info, ChevronRight, Timer } from "lucide-react";

export default function Datamodal({ Breaks }) {
  const [modal, setModal] = useState(false);

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  const allReasons = Object.values(breaks).flatMap((breakType) =>
    breakType.reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
    }))
  );

  const getBreakReasonLabel = (breakReasonId) => {
    const foundReason = allReasons.find(
      (reason) => reason.id === breakReasonId
    );
    return foundReason ? foundReason.label : "Unknown";
  };

  const transformedBreaks = Object.keys(breaks).reduce((acc, key) => {
    const breakItem = breaks[key];
    acc[breakItem.id] = key;
    return acc;
  }, {});

  return (
    <>
      <button
        onClick={toggleModal}
        className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all text-[12px] group"
      >
        <Info className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
        View Details
      </button>

      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleModal}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-[#111111] rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 dark:border-white/5"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-50 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/10 rounded-xl">
                    <Coffee className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold dark:text-white leading-tight">Session Break Log</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Detailed breakdown of all break periods</p>
                  </div>
                </div>
                <button
                  onClick={toggleModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 overflow-auto max-h-[60vh] custom-scrollbar">
                <div className="space-y-3">
                  {!_.isEmpty(Breaks) ? (
                    Breaks.map((times, index) => (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        key={index}
                        className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-white/[0.02] rounded-2xl border border-gray-100 dark:border-white/5 hover:border-amber-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-center min-w-[80px]">
                            <span className="text-xs font-black text-green-600 dark:text-green-400 uppercase tracking-tighter">
                              {moment(times.StartTime).format("h:mm A")}
                            </span>
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Break In</span>
                          </div>

                          <ChevronRight className="w-4 h-4 text-gray-300" />

                          <div className="flex flex-col items-center min-w-[80px]">
                            <span className={`text-xs font-black uppercase tracking-tighter ${times.EndTime ? "text-gray-700 dark:text-gray-300" : "text-blue-500 italic"}`}>
                              {times.EndTime ? moment(times.EndTime).format("h:mm A") : "Active"}
                            </span>
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Break Out</span>
                          </div>

                          <div className="h-8 w-px bg-gray-100 dark:bg-white/5 mx-2" />

                          <div>
                            <div className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-0.5">
                              {transformedBreaks[times?.BreakType]}
                            </div>
                            <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                              {getBreakReasonLabel(times?.BreakReason)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/5 shadow-sm">
                          <Timer className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm font-black text-gray-700 dark:text-gray-300">
                            {times.TotalHours}h {times.TotalMins}m
                          </span>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-full mb-4">
                        <Coffee className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-bold">No break data for this session.</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Excellent attendance productivity!</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 bg-gray-50/50 dark:bg-white/[0.02] border-t border-gray-50 dark:border-white/5 text-center">
                <button
                  onClick={toggleModal}
                  className="px-8 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-black font-bold rounded-xl text-sm hover:scale-105 transition-transform"
                >
                  Close Log
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
